// components/common/TypeaheadSearchField.jsx
//
// A search box that OWNS its input and renders its own suggestion popper.
// The render half of a typeahead; `useTypeaheadSuggestions` is the fetch half.
// The input itself is the shared <SearchField>, so a typeahead box and a plain
// search box look and clear the same way.
//
// No MUI Autocomplete: driving it means reverse-engineering its internal
// `reason` codes. Nothing outside this file mutates the input — the parent
// hands in `value` and gets `onChange`.
//
// Both search boxes on the Purchase orders filter row are this component
// (PO number, then item), which is why they behave identically: typing opens
// a shortlist, Enter searches for the whole term, picking a row commits it,
// the X clears box and filter together.
//
// A box with no `onCommitText` only picks: a popup's "add an item" or "match
// to…" box has no search for the whole term, so Enter acts on a highlighted
// row only and the popper names no Enter action. Escape with the popper open
// closes the popper alone, so a popup around the box stays open.
//
// The popper is a search AID, so it is open only while the draft diverges
// from the committed filter — focusing a box that shows what is already
// applied is not searching, and a popup over the grid there is noise.
//
// It has three empty states and they are NOT interchangeable: too short to
// search, nothing matched, and the search failed. Collapsing the last into the
// second is what makes a broken search read as an answer.

import React, { useCallback, useId, useState } from 'react';
import {
  Box,
  Paper,
  Popper,
  ClickAwayListener,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchField from './SearchField';
import { TYPEAHEAD_MIN_CHARS } from '../../hooks/useTypeaheadSuggestions';
import './TypeaheadSearchField.css';

function TypeaheadSearchField({
  // Wraps the input; owns the box's width in the filter row's CSS.
  className,
  label,
  placeholder,
  // The draft text. Always exactly what the input shows.
  value,
  // What the applied filter reads as. The popper stays shut while the draft
  // equals it.
  committedText = '',
  onChange,
  suggestions,
  loading = false,
  // The last request errored. Reported, never rendered as "nothing found":
  // the merchant would read a failed search as an answer.
  failed = false,
  getOptionKey,
  renderOption,
  // Committing: a picked suggestion, or the typed term as it stands. Without
  // `onCommitText` the box only picks.
  onPick,
  onCommitText,
  showClear,
  onClear,
  clearLabel,
  // Popper copy. `emptyText` names what was not found; `footerText` says what
  // Enter does with the whole term, so a box that only picks has none.
  emptyText,
  footerText,
  autoFocus = false,
}) {
  const { t } = useTranslation();
  const listboxId = useId();

  // Anchor as state (not a ref) — Popper reads it during render, and the
  // element is stable after first mount so this never causes churn.
  const [anchorEl, setAnchorEl] = useState(null);
  const [focused, setFocused] = useState(false);
  // Keyboard highlight, keyed to the suggestions array it indexes into: a
  // fresh suggestions list derives back to "nothing highlighted" without a
  // reset effect, so a stale index never points into a new list.
  const [highlightState, setHighlightState] = useState({ list: null, index: -1 });
  const highlight = highlightState.list === suggestions ? highlightState.index : -1;
  const setHighlight = useCallback((next) => {
    setHighlightState((prev) => {
      const current = prev.list === suggestions ? prev.index : -1;
      return { list: suggestions, index: typeof next === 'function' ? next(current) : next };
    });
  }, [suggestions]);

  const open = focused && value.trim() !== '' && value !== committedText;

  const closePopup = useCallback(() => {
    setFocused(false);
    setHighlightState({ list: null, index: -1 });
  }, []);

  const pick = useCallback(async (option) => {
    await onPick(option);
    closePopup();
  }, [onPick, closePopup]);

  const handleKeyDown = useCallback(async (event) => {
    if (event.key === 'ArrowDown' && open && suggestions.length > 0) {
      event.preventDefault();
      setHighlight((h) => (h + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp' && open && suggestions.length > 0) {
      event.preventDefault();
      setHighlight((h) => (h <= 0 ? suggestions.length - 1 : h - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (open && highlight >= 0 && suggestions[highlight]) {
        await pick(suggestions[highlight]);
      } else if (onCommitText) {
        await onCommitText(value);
        setHighlight(-1);
      }
    } else if (event.key === 'Escape') {
      if (open) event.stopPropagation();
      closePopup();
    }
  }, [open, highlight, suggestions, value, pick, onCommitText, closePopup, setHighlight]);

  const tooShort = value.trim().length < TYPEAHEAD_MIN_CHARS;

  return (
    <ClickAwayListener onClickAway={closePopup}>
      <Box className={className} ref={setAnchorEl}>
        <SearchField
          label={label}
          placeholder={placeholder}
          value={value}
          onChange={(text) => {
            // Escape closes the popup by flipping `focused` while the
            // input keeps DOM focus (no new focus event will fire) —
            // typing again must reopen it.
            setFocused(true);
            onChange(text);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          autoFocus={autoFocus}
          loading={loading}
          showClear={showClear}
          onClear={onClear}
          clearLabel={clearLabel}
          htmlInputProps={{
            role: 'combobox',
            'aria-autocomplete': 'list',
            'aria-expanded': open,
            'aria-controls': open ? listboxId : undefined,
            'aria-activedescendant': highlight >= 0 ? `${listboxId}-${highlight}` : undefined,
            autoComplete: 'off',
          }}
        />
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement="bottom-start"
          className="typeahead-popper"
          style={{ width: anchorEl?.clientWidth }}
        >
          <Paper>
            {failed ? (
              <div className="typeahead-empty">{t('searchFilters.searchFailed')}</div>
            ) : tooShort ? (
              <div className="typeahead-empty">{t('searchFilters.typeMinChars', { count: TYPEAHEAD_MIN_CHARS })}</div>
            ) : suggestions.length === 0 ? (
              // Nothing to list yet. While the first request is still out that
              // is not an answer, so the popper says nothing (the input's
              // spinner already reports it) rather than rendering "none found"
              // over a search in flight, or an empty listbox with no options.
              loading ? null : <div className="typeahead-empty">{emptyText}</div>
            ) : (
              <ul className="typeahead-listbox" role="listbox" id={listboxId}>
                {suggestions.map((option, index) => (
                  <li
                    key={getOptionKey(option)}
                    id={`${listboxId}-${index}`}
                    role="option"
                    aria-selected={index === highlight}
                    className={index === highlight ? 'typeahead-option is-highlighted' : 'typeahead-option'}
                    // onMouseDown beats the TextField blur → ClickAwayListener race
                    onMouseDown={(e) => { e.preventDefault(); pick(option); }}
                    onMouseEnter={() => setHighlight(index)}
                  >
                    {renderOption(option)}
                  </li>
                ))}
              </ul>
            )}
            {footerText && <Box className="typeahead-helper">{footerText}</Box>}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

export default TypeaheadSearchField;
